import {
  FluidFormElementConfig,
  FluidKeyedObject,
  isBooleanValue,
} from '@lmig/fluid-core';

import { ATTR } from '../../../constants/constants';
import {
  getDataAtPath,
  setDataAtPath,
} from '../../fluid-table/internal/fluid-table-data-management.utils';
import { FluidForm } from '../fluid-form';

/**
 * Cache Key Contants
 */
export const DISABLED_CACHE = '_disabledCache';
export const ELEMENT_CACHE = '_elementConfigCache';
export const HIDDEN_FIELDS_CACHE = '_hiddenFieldsCache';

/**
 * Fluid form cache interface - internal use only
 * @internal
 */
export interface FluidFormStateCache {
  _originalState?: Partial<FluidFormStateCache>;
  [DISABLED_CACHE]: Record<string, boolean>;
  [ELEMENT_CACHE]: Record<string, FluidFormElementConfig>;
  [HIDDEN_FIELDS_CACHE]: FluidKeyedObject<boolean>;
  _cacheEnabled: FluidFormStateCacheToggle;
}

/**
 * Toggle interface for cache disabled/enabled
 */
export interface FluidFormStateCacheToggle {
  [DISABLED_CACHE]: boolean;
  [ELEMENT_CACHE]: boolean;
  [HIDDEN_FIELDS_CACHE]: boolean;
}

/**
 * Default cache state for first render
 */
export const formStateCache = {
  [DISABLED_CACHE]: {},
  [ELEMENT_CACHE]: {},
  [HIDDEN_FIELDS_CACHE]: {},
  _cacheEnabled: {
    [DISABLED_CACHE]: false,
    [ELEMENT_CACHE]: false,
    [HIDDEN_FIELDS_CACHE]: true,
  },
};

/**
 * Updates the host form's cache at a particular dataPath
 * so we can track and apply modifications state between renders.
 * @param host - the host form
 * @param dataPath - the dataPath of the element to update
 * @param cacheKey - the cache to update
 * @param value - the new value to cache
 * @param falsyToUndefined - if true, falsy values will be converted to undefined
 */
export const updateCache = <Host extends FluidForm>(
  host: Host,
  dataPath: string,
  cacheKey: string,
  value: any,
  falsyToUndefined = false
) => {
  if (falsyToUndefined) value = value ? value : undefined;
  host._formStateCache = setDataAtPath(
    host._formStateCache,
    null,
    value,
    `${cacheKey}.${dataPath}`
  );
};

export const checkDisabledUpdateForCache = <
  Host extends FluidForm | HTMLFluidFormElement
>(
  host: Host,
  key: string,
  value: any,
  configObj: Partial<FluidFormElementConfig>,
  dataPath: string
) => {
  if (
    key === ATTR.disabled ||
    (!!configObj && Object.keys(configObj).indexOf(ATTR.disabled) !== -1)
  ) {
    host.setDisabled(
      key === ATTR.disabled ? value : configObj.disabled,
      dataPath
    );
  }
};

/**
 * Sets the original state cache, in case we need to clear cache and revert elements to
 * originally configured state.
 * @param host
 * @param dataPath
 * @param cacheKey
 * @param value
 */
export const setOriginalElementCache = (
  host: Partial<FluidForm>,
  cacheKey: string,
  dataPath: string,
  value: any
) => {
  host._formStateCache._originalState = {
    ...host._formStateCache._originalState,
    [cacheKey]: {
      ...host._formStateCache._originalState?.[cacheKey],
      [dataPath]: value,
    },
  };
};

/**
 * Resets the disabled state cache back to its original, first-render, state.
 * @param host
 */
export const clearDisabledStateCache = <Host extends FluidForm>(
  host: Host
): FluidFormElementConfig[] => {
  Object.keys(host._formStateCache._originalState[DISABLED_CACHE]).forEach(
    (dataPath) => {
      updateCache(
        host,
        dataPath,
        DISABLED_CACHE,
        getDataAtPath(
          host._formStateCache._originalState[DISABLED_CACHE],
          null,
          dataPath,
          true
        ),
        true
      );
    }
  );
  return applyCacheStates(host, host.elements);
};

export const clearElementConfigCache = <Host extends FluidForm>(
  host: Host
): FluidFormElementConfig[] => {
  Object.keys(host._formStateCache._originalState[ELEMENT_CACHE]).forEach(
    (dataPath) => {
      updateCache(
        host,
        dataPath,
        ELEMENT_CACHE,
        getDataAtPath(
          host._formStateCache._originalState[ELEMENT_CACHE],
          null,
          dataPath
        )
      );
    }
  );
  return applyCacheStates(host, host.elements);
};

/**
 * Set any initial cache values based on the original element configuration
 * @param host - the host form from which to gather the states
 */
export const setInitialCacheAtRender = <Host extends FluidForm>(host: Host) => {
  host.elements.forEach((el: FluidFormElementConfig) => {
    setOriginalElementCache(host, DISABLED_CACHE, el.dataPath, el.disabled);
    setOriginalElementCache(host, ELEMENT_CACHE, el.dataPath, el);
    updateCache(host, el.dataPath, DISABLED_CACHE, el.disabled);
    updateCache(host, el.dataPath, HIDDEN_FIELDS_CACHE, true);
  });
};

/**
 * Returns true if the element should be disabled
 * @param host - the host form
 * @param dataPath - the dataPath of the element state to check
 */
export const isDisabledCache = (
  host: Partial<FluidForm>,
  dataPath: string
): boolean => {
  const cacheValue = getDataAtPath(
    host._formStateCache[DISABLED_CACHE],
    null,
    dataPath,
    true
  );
  return cacheEnabled(host, DISABLED_CACHE) && isBooleanValue(cacheValue)
    ? cacheValue
    : undefined;
};

/**
 * Checks if a particular dataPath has a value explicitly defined in cache
 * @param host
 * @param dataPath
 */
export const hasCachedDisabledValue = (
  host: Partial<FluidForm>,
  dataPath
): boolean => {
  return isBooleanValue(
    getDataAtPath(host._formStateCache[DISABLED_CACHE], null, dataPath, true)
  );
};

/**
 * Apply any element state held in cache, that may be lost during re-renders ONLY if
 * a value exists for it.
 * @param host - the host form
 * @param elements - the elements to apply the cached state to
 */
export const applyCacheStates = <Host extends FluidForm>(
  host: Host,
  elements: FluidFormElementConfig[]
) =>
  elements.map((el) => {
    return applyDisabledStateCache(host, {
      ...applyElementConfigCache(host, el),
      hidden: isHiddenCache(host, el.dataPath),
    });
  });

export const applyDisabledStateCache = (
  host: Partial<FluidForm>,
  element: FluidFormElementConfig
): FluidFormElementConfig => {
  return !!hasCachedDisabledValue(host, element.dataPath) &&
    cacheEnabled(host, DISABLED_CACHE)
    ? {
        ...element,
        disabled: isDisabledCache(host, element.dataPath),
      }
    : {
        ...element,
        disabled: !!element.disabled,
      };
};

/**
 * Get the cached config value at the given dataPath.
 * @param host
 * @param dataPath
 */
export const elementConfigCache = (
  host: Partial<FluidForm>,
  dataPath: string
): FluidFormElementConfig | undefined => {
  return getDataAtPath(host._formStateCache[ELEMENT_CACHE], null, dataPath);
};

export const isHiddenCache = <Host extends FluidForm>(
  host: Host,
  dataPath: string
) =>
  getDataAtPath(
    host._formStateCache[HIDDEN_FIELDS_CACHE],
    null,
    dataPath,
    true
  );

/**
 * If cached config exists, and cache is enabled, return the cached version, otherwise, return the
 * current element config
 * @param host
 * @param element
 */
export const applyElementConfigCache = (
  host: Partial<FluidForm>,
  element: FluidFormElementConfig
): FluidFormElementConfig => {
  return !!elementConfigCache(host, element.dataPath) &&
    cacheEnabled(host, ELEMENT_CACHE)
    ? elementConfigCache(host, element.dataPath)
    : element;
};

/**
 * Cache toggle queries (true if cache is enabled)
 * @param host
 * @param cacheKey
 */
export const cacheEnabled = (host: Partial<FluidForm>, cacheKey: string) =>
  host._formStateCache._cacheEnabled[cacheKey];

/**
 * Cache toggles
 */
export const toggleCache = <Host extends FluidForm>(
  host: Host,
  cacheKey: string,
  enabled: boolean
) => (host._formStateCache._cacheEnabled[cacheKey] = enabled);
